import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchMyProfileCall, followOrunFollowUserCall } from "../api/userApiCalls";
import { useUser } from "../context/UserContext";

/**
 * Refreshes the signed-in user's own profile.
 *
 * Counts like posts, followers, following and likedPost live on the user object in
 * UserContext, not in any post or profile query — so invalidating a query never updates them.
 * Anything that changes one of those numbers has to refresh the profile explicitly, or the
 * header keeps showing the old value until the next cold start.
 *
 * Failures are swallowed: the action itself already succeeded, and the number self-corrects on
 * the next profile load. A 401 is handled globally by the response interceptor.
 */
export const useRefreshMyProfile = () => {
  const { setUser } = useUser();
  return () =>
    fetchMyProfileCall()
      .then((response) => setUser({ ...response.data }))
      .catch(() => {});
};

/**
 * Follow or unfollow another user, keeping every view of the change in sync:
 *
 *  - the target's profile, whose follower count changed
 *  - any follower/following list, which gained or lost a row
 *  - the signed-in user's own profile, whose `following` count changed — the one that is easy
 *    to miss, because nothing about it lives in a query
 */
export const useFollowUser = () => {
  const queryClient = useQueryClient();
  const refreshMyProfile = useRefreshMyProfile();

  return useMutation({
    mutationFn: ({ userId, action }: { userId: number | string; action: "follow" | "unfollow" }) =>
      followOrunFollowUserCall(String(userId), action),
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["user", String(userId)] });
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      return refreshMyProfile();
    },
  });
};
